/**
 * The Stage's geometry: which colour sits at the bottom of the board, and how
 * wide the column is.
 *
 * It subscribes to the board orientation (my colour and the user's flip) and to
 * the measured board size, both of which change at most once per game plus
 * whenever the user presses Flip or resizes the window. Everything that changes
 * per move is inside a leaf.
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {orientationFor, selectBoardSource} from '@/Store/liveGame/selectors';
import {id} from '@/Store/liveGame/state';
import type {Side} from '@/Store/liveGame/state';

import {useAnalysis} from '../../rail/analysis/analysisStore';
import {showEvalBar} from '../../rail/surface';
import {LAYOUT, useV2Ui} from '../../theme/useV2Ui';

export type StageModel = {
  top: Side;
  bottom: Side;
  /** the column is exactly as wide as the board frame */
  width: number;
  /**
   * The eval bar exists (fix D / R4). ONE BOOLEAN off `analysisStore`, which
   * cannot move while a game is live — that is the whole render budget the
   * analysis feature costs the Stage. Fix K: ALSO ANDed with `rail/surface.ts`'s
   * `showEvalBar` — `analysisStore.on` can outlive a watch that just ended
   * (see that predicate's own docblock), and an eval bar over the idle
   * tournament wait's board is exactly the inconsistency this fix closes.
   */
  analysis: boolean;
};

/**
 * Which colour the board FACES, before the user's flip.
 *
 * M4: in watch mode it is white at the bottom, unless the watcher is himself the
 * black seat of the game on screen — which is the replay path, where the old
 * screen reuses the whole `watch*` state to show a FINISHED game of your own
 * (`openArenaGameByTable`, index.js L3420-3467). Same rule as the core's
 * `selectWatchBoardProps.boardOrientation`, read as ONE field so a watch write
 * cannot re-render the Stage while nothing is being watched.
 */
export const selectStageColor = (s: LiveGameState): Side | null =>
  selectBoardSource(s) === 'watch'
    ? !!s.session.me && id(s.watch.black?.id) === s.session.me
      ? 'black'
      : 'white'
    : s.game.myColor;

export function useStageModel(): StageModel {
  const seatColor = useLiveGame(selectStageColor);
  const engineOn = useAnalysis(s => s.on);
  // Fix K: one more boolean subscription, dedicated to this one predicate
  // (ARCHITECTURE §5) — `tournamentIdle` does not move per move, only at the
  // same phase transitions `seatColor` already does.
  const engineAllowed = useLiveGame(showEvalBar);
  const analysis = engineOn && engineAllowed;
  const {flipped, boardSize, viewport} = useV2Ui(
    useShallow(s => ({
      flipped: s.flipped,
      boardSize: s.boardSize,
      viewport: s.viewport,
    })),
  );

  // the colour at the BOTTOM of the board is the one the board faces
  const bottom = orientationFor(seatColor, flipped);
  const framePadding =
    viewport === 'phone'
      ? LAYOUT.boardFramePaddingPhone
      : LAYOUT.boardFramePadding;

  return {
    top: bottom === 'white' ? 'black' : 'white',
    bottom,
    width: boardSize + 2 * framePadding,
    analysis,
  };
}
